import { action } from "typesafe-actions";
import {
  ILead,
  ILeadSource,
  ILeadStatusCount,
  IRoughEstimate,
  IWhatsAppStatusUnrepliedCount,
} from "./leads.types";
import { IDate } from "../../components/Table/hooks/useDateFilter";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  convertLoaderCompleted,
  convertLoaderProgress,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { makeApiCall } from "../../helpers/postRequest";
import { endPoints } from "../../constants/endPoints";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { getDataTablev2InitialDate } from "../../components/TableV2/helpers/dataTableV2DatesFilter";
import { getUniqueId } from "../../helpers";
import axios, { CancelTokenSource } from "axios";
import moment from "moment";
import { uploadMultipleFile } from "../../components/FileUpload/utils";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { on } from "events";
import { IWhatsAppActivity } from "../activity/activity.types";

const getMonthStartEndDate = (month: string, year: string) => {
  const formattedMonth = month.padStart(2, "0");

  const start = moment(`${year}-${formattedMonth}-01`)
    .startOf("month")
    .format("YYYY-MM-DD");
  const end = moment(`${year}-${formattedMonth}-01`)
    .endOf("month")
    .format("YYYY-MM-DD");

  return { start, end };
};

const getYearStartEndDate = (year: string) => {
  const start = moment(`${year}-01-01`).startOf("year").format("YYYY-MM-DD");
  const end = moment(`${year}-12-31`).endOf("year").format("YYYY-MM-DD");

  return { start, end };
};

export const FETCH_LEADS_LIST_PROGRESS = "FETCH_LEADS_LIST_PROGRESS";
export const FETCH_LEADS_LIST_SUCCESS = "FETCH_LEADS_LIST_SUCCESS";
export const FETCH_LEADS_LIST_FAILED = "FETCH_LEADS_LIST_FAILED";

export const fetchLeadsListProgress = () => action(FETCH_LEADS_LIST_PROGRESS);
export const fetchLeadsListSuccess = (list: ILead[], totalRecords: number) =>
  action(FETCH_LEADS_LIST_SUCCESS, { list, totalRecords });
export const fetchLeadsListFailed = () => action(FETCH_LEADS_LIST_FAILED);

let cancelToken: CancelTokenSource | null = null;

interface IFetchLeadsListAsyncParams {
  pageNumber: number;
  rowsInPerPage: number;
  limit: number;
  status: string;
  searchValue: IDatatableV2AdvancedSearchFilter;
  companyOutletUuid: string;
  isRecovery?: boolean;
  searchByName?: string;
  searchByNo?: string;
  searchByVenue?: string;
  otherCustomStateProperties?: { [key: string]: any };
  searchByEventName?: string;
  multiple_events?: number | null;
  date?: IDataTableV2DateState["dates"];
}
export const fetchLeadsListAsync =
  (
    params: IFetchLeadsListAsyncParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      // Cancel previous request if it exists
      if (cancelToken) {
        cancelToken.cancel("Operation canceled due to a new request.");
      }

      // Create a new CancelToken for the current request
      cancelToken = axios.CancelToken.source();

      const {
        followupDate,
        searchByIsReplied: searchByWhatsappStatus,
        searchByEventType,
        searchByStatus,
        functionDate,
      } = params.otherCustomStateProperties || {};

      const month = functionDate?.month;
      const year = functionDate?.year;

      let url = "";

      if (params.date && params.date?.fromDate && params.date?.toDate) {
        url += `&from_date=${params.date.fromDate}&to_date=${params.date.toDate}`;
      }

      if (params.status !== "-1") {
        url += `&status=${params.status}`;
      }

      if (params.companyOutletUuid) {
        url += `&company_outlet_uuid=${params.companyOutletUuid}`;
      }

      if (
        params.searchValue.length > 0 ||
        params.searchByName ||
        params.searchByVenue ||
        month ||
        year ||
        followupDate ||
        params.searchByNo ||
        searchByEventType ||
        searchByStatus
      ) {
        const advanceFilter = [...params.searchValue];

        if (params.searchByName) {
          advanceFilter.push({
            column: ["name_of_guest"],
            value: params.searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (params.searchByVenue) {
          advanceFilter.push({
            column: ["preferred_venue"],
            value: params.searchByVenue,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && !year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `-${month}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && year) {
          const { start, end } = getMonthStartEndDate(month, year);
          advanceFilter.push({
            column: ["function_date"],
            value: start,
            logicalOperator: "AND",
            operator: "GREATER_THAN_EQUAL",
          });
          advanceFilter.push({
            column: ["function_date"],
            value: end,
            logicalOperator: "AND",
            operator: "LESSER_THAN_EQUAL",
          });
        }

        if (year && !month) {
          const { start, end } = getYearStartEndDate(year);
          advanceFilter.push({
            column: ["function_date"],
            value: start,
            logicalOperator: "AND",
            operator: "GREATER_THAN_EQUAL",
          });
          advanceFilter.push({
            column: ["function_date"],
            value: end,
            logicalOperator: "AND",
            operator: "LESSER_THAN_EQUAL",
          });
        }

        if (followupDate) {
          advanceFilter.push({
            column: ["followup_date"],
            value: followupDate,
            logicalOperator: "AND",
            operator: "EQUAL",
          });
        }

        if (params.searchByNo) {
          advanceFilter.push({
            column: ["contact_number_one"],
            value: params.searchByNo,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (searchByStatus) {
          advanceFilter.push({
            column: ["status"],
            value: searchByStatus,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (searchByEventType && searchByEventType.length > 0) {
          let finalEventType = [...searchByEventType];
          if (params.searchByEventName) {
            const result = ((params.searchByEventName || "") as string)
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item !== "");

            if (result.length > 0) {
              const otherSocial = (searchByEventType as any).findIndex(
                (x: string) => x === "OTHER SOCIAL"
              );
              if (otherSocial > -1) {
                (finalEventType as string[]).splice(otherSocial, 1);
              }

              finalEventType = [...finalEventType, ...result];
            }
          }
          advanceFilter.push({
            column: ["event_type"],
            value: finalEventType as any,
            logicalOperator: "AND",
            operator: "IN",
          });
        }

        url += `&advanceFilter=${encodeURIComponent(
          JSON.stringify(advanceFilter)
        )}`;
      }

      if (searchByWhatsappStatus) {
        url += `&unreplied_messages=${searchByWhatsappStatus}`;
      }

      if (params.isRecovery) {
        url += `&is_archived=true`;
      }

      if (
        params.multiple_events !== undefined &&
        params.multiple_events !== null
      ) {
        url += `&multiple_events=${params.multiple_events}`;
      }

      let finalUrl =
        `${endPoints.leads.fetchleads}?pageNo=${params.pageNumber}&pageLimit=${params.limit}&itemPerPage=${params.rowsInPerPage}` +
        url;

      if (
        params.searchByName ||
        params.searchByVenue ||
        month ||
        year ||
        followupDate ||
        params.searchByNo ||
        searchByWhatsappStatus ||
        searchByStatus ||
        params.status !== "-1"
      ) {
        finalUrl =
          `${endPoints.leads.fetchleads}?pageNo=${params.pageNumber}&pageLimit=${params.limit}&itemPerPage=${params.rowsInPerPage}` +
          url;
      }

      dispatch(fetchLeadsListProgress());
      dispatch(saveLoaderProgress());

      const res = await api.get(finalUrl, { cancelToken: cancelToken.token });
      const data: ILead[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchLeadsListSuccess(data, totalRecords));
      dispatch(saveLoaderCompleted());
    } catch (err: any) {
      if (axios.isCancel(err)) {
        console.log("Previous request canceled", err.message);
        // Don't dispatch saveLoaderCompleted here - let the new request handle loading state
      } else {
        dispatch(fetchLeadsListFailed());
        dispatch(saveLoaderCompleted());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "An error occurred",
            displayAs: "snackbar",
          })
        );
      }
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchDashboardLeadsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    status: string,
    searchValue: IDatatableV2AdvancedSearchFilter,
    date: string,
    company_outlet_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      // const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchLeadsListProgress());
      dispatch(saveLoaderProgress());

      let url = "";
      if (status !== "-1") {
        url += `&status=${status}`;
      }
      if (searchValue.length > 0) {
        url += `&advanceFilter=${encodeURIComponent(
          JSON.stringify(searchValue)
        )}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      let finalUrl =
        `${endPoints.leads.fetchleads}?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&column=followup_date&value=` +
        date +
        url;
      const res = await api.get(finalUrl);
      const data: ILead[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchLeadsListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchLeadsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_LEAD_PROGRESS = "FETCH_LEAD_PROGRESS";
export const FETCH_LEAD_SUCCESS = "FETCH_LEAD_SUCCESS";
export const FETCH_LEAD_FAILED = "FETCH_LEAD_FAILED";

export const fetchLeadProgress = () => action(FETCH_LEAD_PROGRESS);
export const fetchLeadSuccess = (lead: ILead) =>
  action(FETCH_LEAD_SUCCESS, { lead });
export const fetchLeadFailed = (errorMessage: string) =>
  action(FETCH_LEAD_FAILED, { errorMessage });

interface IFetchLeadAsyncParams {
  leadId: string;
  company_outlet_uuid: string;
  multiple_events?: number;
  onCallBack?: (lead: ILead, isSuccess: boolean) => void;
  skipLoader?: boolean;
}

export const fetchLeadAsync =
  (
    params: IFetchLeadAsyncParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchLeadProgress());
      if (!params.skipLoader) {
        dispatch(saveLoaderProgress());
      }

      let url = `${endPoints.leads.fetchleads}?leads_uuid=${params.leadId}&company_outlet_uuid=${params.company_outlet_uuid}`;

      if (
        params.multiple_events !== undefined &&
        params.multiple_events !== null
      ) {
        url += `&multiple_events=${params.multiple_events}`;
      }

      const res = await api.get(url);
      const data = res.data.data;

      if (data.length > 0) {
        const lastLead = data[data.length - 1];

        const combinedLead: ILead = {
          ...lastLead, // Use last lead for common fields
          multiple_events_list: data.map((lead: ILead) => ({
            // Individual lead UUID (unique per event)
            leads_uuid: lead.leads_uuid,
            // Event identification
            event_type: lead.event_type,
            event_uuid: lead.event_uuid,
            event_time: lead.event_time,
            event_time_uuid: lead.event_time_uuid,
            preferred_venue: lead.preferred_venue,
            preferred_venue_uuid: lead.preferred_venue_uuid,
            // Event-specific fields (independent per row)
            function_month: lead.function_month,
            function_date: lead.function_date,
            followup_date: lead.followup_date,
            status: lead.status,
            derived_status: lead.derived_status,
            drive_status_uuid: lead.drive_status_uuid,

            number_of_rooms: lead.number_of_rooms,
            number_of_guests: lead.number_of_guests,
            // Conditional fields
            cancellation_dead_reason: lead.cancellation_dead_reason,
            payment_mode: lead.payment_mode,
            other_event_name: lead.other_event_name,
          })),
        };

        dispatch(fetchLeadSuccess(combinedLead));
        params.onCallBack?.(combinedLead, true);
      } else {
        dispatch(
          fetchLeadFailed(
            "Unfortunately, there are no records available at the moment."
          )
        );
        params.onCallBack?.(data[0], false);
      }
    } catch (err: any) {
      dispatch(fetchLeadFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      if (!params.skipLoader) {
        dispatch(saveLoaderCompleted());
      }
    }
  };

export const upsertLeadAsync =
  (
    existingLead: ILead | ILead[], // Accept both single lead and array of leads
    initialLead: ILead,
    isUpdate: boolean,
    isopportunity: boolean,
    onCallBack: (isSuccess?: boolean, data?: ILead | ILead[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      // Ensure we always work with an array
      const leadsArray = Array.isArray(existingLead)
        ? existingLead
        : [existingLead];

      const processedLeads = leadsArray.map((lead) => {
        const {
          create_ts,
          insert_ts,
          modified_by_name,
          modified_by_uuid,
          created_by_name,
          created_by_uuid,

          ...rest
        } = lead;

        return rest;
      });

      const res = await makeApiCall(
        {
          url: endPoints.leads.upsertLead,
          method: "POST",
          automation: {
            primaryFieldName: "lead_id",
            isUpdate: isUpdate,
          },
        },
        initialLead,
        processedLeads // Send as array
      );
      dispatch(
        showMessage({
          type: "success",
          message:
            leadsArray.length > 1
              ? "Leads are saved successfully"
              : "Lead is saved successfully",
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);

      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to save lead(s)",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const convertToOpportunityAsync =
  (
    leadUUID: string,
    onCallBack: (isSuccess?: boolean, data?: ILead) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(convertLoaderProgress());
    try {
      const res = await api.post("/leads/convert-leads-into-opportunity", {
        leads_uuid: leadUUID,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Successfully converted to opportunity!",
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(convertLoaderCompleted());
    }
  };

export const FETCH_LEAD_STATUS_COUNT_PROGRESS =
  "FETCH_LEAD_STATUS_COUNT_PROGRESS";
export const FETCH_LEAD_STATUS_COUNT_SUCCESS =
  "FETCH_LEAD_STATUS_COUNT_SUCCESS";
export const FETCH_LEAD_STATUS_COUNT_FAILED = "FETCH_LEAD_STATUS_COUNT_FAILED";

export const fetchLeadStatusCountProgress = () =>
  action(FETCH_LEAD_STATUS_COUNT_PROGRESS);
export const fetchLeadStatusCountSuccess = (data: ILeadStatusCount[]) =>
  action(FETCH_LEAD_STATUS_COUNT_SUCCESS, { data });
export const fetchLeadStatusCountFailed = () =>
  action(FETCH_LEAD_STATUS_COUNT_FAILED);

export const fetchLeadStatusCountAsync =
  (
    date: IDataTableV2DateState["dates"],

    companyOutlet?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchLeadStatusCountProgress());
      let url = "";

      if (companyOutlet) {
        url += `&company_outlet_uuid=${companyOutlet}`;
      }

      const finalUrl =
        `/leads/get-status-leads?type=count&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;

      const res = await api.get(finalUrl);

      const data: ILeadStatusCount[] = res.data.data.statusCounts;

      dispatch(fetchLeadStatusCountSuccess(data || []));
    } catch (err: any) {
      dispatch(fetchLeadStatusCountFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_LEADS_LIST_STATUS_COUNT_PROGRESS =
  "FETCH_LEADS_LIST_STATUS_COUNT_PROGRESS";
export const FETCH_LEADS_LIST_STATUS_COUNT_SUCCESS =
  "FETCH_LEADS_LIST_STATUS_COUNT_SUCCESS";
export const FETCH_LEADS_LIST_STATUS_COUNT_FAILED =
  "FETCH_LEADS_LIST_STATUS_COUNT_FAILED";

export const fetchLeadsListStatusCountProgress = () =>
  action(FETCH_LEADS_LIST_STATUS_COUNT_PROGRESS);
export const fetchLeadsListStatusCountSuccess = (
  list: ILead[],
  totalRecords: number
) => action(FETCH_LEADS_LIST_STATUS_COUNT_SUCCESS, { list, totalRecords });
export const fetchLeadsListStatusCountFailed = () =>
  action(FETCH_LEADS_LIST_STATUS_COUNT_FAILED);

export const fetchLeadListStatusCountAsync =
  (
    type: string,
    monthType: string,
    status: string,
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    company_outlet_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      // const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchLeadsListStatusCountProgress());
      dispatch(saveLoaderProgress());

      let url = "";
      if (status !== "-1") {
        url += `&status_type=${status}`;
      }
      if (monthType) {
        url += `&data_wise=${monthType}`;
      }
      if (searchValue.length > 0) {
        url += `&advanceFilter=${encodeURIComponent(
          JSON.stringify(searchValue)
        )}`;
      }
      if (type) {
        url += `&type=${type}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      let finalUrl =
        `/leads/get-status-leads?remark_needed=true&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;
      const res = await api.get(finalUrl);
      const data: ILead[] = res.data.data.leadList;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchLeadsListStatusCountSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchLeadsListStatusCountFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// **************************************************Rough Estimate**************************************************

export const FETCH_ROUGH_ESTIMATE_PROGRESS = "FETCH_ROUGH_ESTIMATE_PROGRESS";
export const FETCH_ROUGH_ESTIMATE_SUCCESS = "FETCH_ROUGH_ESTIMATE_SUCCESS";
export const FETCH_ROUGH_ESTIMATE_FAILED = "FETCH_ROUGH_ESTIMATE_FAILED";

export const fetchRoughEstimateProgress = () =>
  action(FETCH_ROUGH_ESTIMATE_PROGRESS);
export const fetchRoughEstimateSuccess = (roughEstimates: IRoughEstimate[]) =>
  action(FETCH_ROUGH_ESTIMATE_SUCCESS, { roughEstimates });
export const fetchRoughEstimateFailed = (errorMessage: string) =>
  action(FETCH_ROUGH_ESTIMATE_FAILED, { errorMessage });

export const fetchRoughEstimateAsync =
  (
    leadUuid: string,
    multiple_events: number | null,
    roughEstimateId?: string,
    skipLoader?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchRoughEstimateProgress());
      if (!skipLoader) {
        dispatch(saveLoaderProgress());
      }

      let url = `/leads/get-rough-estimate?leads_uuid=${leadUuid}`;

      if (roughEstimateId) {
        url += `&rough_estimate_uuid=${roughEstimateId}`;
      }

      if (multiple_events !== undefined && multiple_events !== null) {
        url += `&multiple_events=${multiple_events}`;
      }

      const res = await api.get(url);
      const data = res.data.data;
      if (data.length > 0) {
        // Process attachments for all rough estimates
        const roughEstimatesWithAttachments = data.map((estimate: any) => {
          const attachments: IFileUpload[] = [];
          for (const item of (estimate.attachments || []) as any) {
            attachments.push({
              file: null,
              key: getUniqueId(),
              path: item,
            });
          }
          return {
            ...estimate,
            attachments: attachments,
          };
        });

        dispatch(fetchRoughEstimateSuccess(roughEstimatesWithAttachments));
      } else {
        dispatch(
          fetchRoughEstimateFailed("Oops! We couldn't find any records .")
        );
      }
    } catch (err: any) {
      dispatch(fetchRoughEstimateFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      if (!skipLoader) {
        dispatch(saveLoaderCompleted());
      }
    }
  };

export const upsertRoughEstimateAsync =
  (
    roughEstimate: IRoughEstimate,

    onCallBack: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const {
        create_ts,
        insert_ts,
        modified_by_name,
        modified_by_uuid,
        created_by_name,
        created_by_uuid,
        ...rest
      } = roughEstimate;

      const asPayload = {
        title: roughEstimate.rough_estimate_uuid,
      };

      const { paths } = await uploadMultipleFile(
        roughEstimate.attachments || [],
        "ROUGH_ESTIMATION",
        asPayload
      );

      const res = await api.post("/leads/upsert-rough-estimate", {
        ...rest,
        attachments: paths,
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Rough estimate saved successfully",
          displayAs: "snackbar",
        })
      );

      const data = res.data.data;

      onCallBack(true, data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong!",
          displayAs: "snackbar",
        })
      );
      onCallBack(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchRoughEstimatePreviewAsync =
  (
    uuid: string,
    type: string,
    multiple_events: number,
    onCallback: (isSuccess: boolean, html?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(
        `/leads/rough-estimate/${uuid}/${type}?multiple_events=${multiple_events}`
      );

      onCallback(true, res.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const fetchRoughEstimateDownloadPdfAsync =
  (
    uuid: string,
    type: string,
    multiple_events: number,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/leads/rough-estimate/${uuid}/${type}?multiple_events=${multiple_events}`,
        {
          responseType: "arraybuffer",
          validateStatus: (status) => status === 200,
        }
      );

      const contentType = res.headers["content-type"];

      if (!contentType.includes("application/pdf")) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      dispatch(saveLoaderCompleted());

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "rough-estimate.pdf";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      onCallback(true);
    } catch (err: any) {
      dispatch(saveLoaderCompleted());

      let errorMessage = "Failed to download PDF";
      if (err.response?.data) {
        const decoder = new TextDecoder("utf-8");
        const errorData = decoder.decode(err.response.data);
        try {
          errorMessage = JSON.parse(errorData).message || errorMessage;
        } catch {
          errorMessage = errorData;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    }
  };

export const fetchVenueAvailabilityAsync =
  (
    venueId: string,
    eventTimeUuid: string,
    function_date: string,
    onCallback: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchLeadsListProgress());

      let url = `/occupancy/check-availability?venue_uuid=${venueId}&event_time_uuid=${eventTimeUuid}&from_date=${function_date}&to_date=${function_date}`;

      const res = await api.get(url);

      const data = res.data.data;

      if (data.availabilty === true) {
        onCallback(true, null);
        dispatch(
          showMessage({
            type: "success",
            message: "Venue is Available for the selected date and Venue on this time",
            displayAs: "snackbar",
          })
        );
      } else {
        onCallback(false, data?.details);
      }
    } catch (err: any) {}
  };

export const sendRoughEstimateWhatsAppAsync =
  (
    roughEstimateUuid: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const res = await api.post(
        `/leads/rough-estimate/${roughEstimateUuid}/send-whatsapp`
      );

      dispatch(
        showMessage({
          type: "success",
          message: "Rough estimate sent successfully via WhatsApp",
          displayAs: "snackbar",
        })
      );

      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to send rough estimate via WhatsApp",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// UNREPLIED WHATSAPP API

export const FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_PROGRESS =
  "FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_PROGRESS";
export const FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_SUCCESS =
  "FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_SUCCESS";
export const FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_FAILED =
  "FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_FAILED";

export const fetchWhatsappStatusUnrepliedCountProgress = () =>
  action(FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_PROGRESS);
export const fetchWhatsappStatusUnrepliedCountSuccess = (
  data: IWhatsAppStatusUnrepliedCount[]
) => action(FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_SUCCESS, { data });
export const fetchWhatsappStatusUnrepliedCountFailed = () =>
  action(FETCH_WHATSAPP_STATUS_UNREPLIED_COUNT_FAILED);

export const fetchWhatsappStatusUnrepliedWhatsappCountAsync =
  (
    date: IDataTableV2DateState["dates"],
    companyOutlet?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchWhatsappStatusUnrepliedCountProgress());
      let url = "";
      if (companyOutlet) {
        url += `&company_outlet_uuid=${companyOutlet}`;
      }
      const finalUrl =
        `/leads/get-status-unreplied-leads?type=count&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;
      const res = await api.get(finalUrl);
      const data: IWhatsAppStatusUnrepliedCount[] = res.data.data.statusCounts;

      dispatch(fetchWhatsappStatusUnrepliedCountSuccess(data || []));
    } catch (err: any) {
      dispatch(fetchWhatsappStatusUnrepliedCountFailed());
    }
  };

export const upsertLeadSourceAsync =
  (
    leadSource: ILeadSource,
    onCallBack: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      const { ...rest } = leadSource;

      const res = await api.post("/leads/upsert-lead-source", {
        ...rest,
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Lead source saved successfully",
          displayAs: "snackbar",
        })
      );

      const data = res.data.data;

      onCallBack(true, data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong!",
          displayAs: "snackbar",
        })
      );
      onCallBack(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// **************************************************Drive Status**************************************************

export const FETCH_DRIVE_STATUS_LIST_PROGRESS =
  "FETCH_DRIVE_STATUS_LIST_PROGRESS";
export const FETCH_DRIVE_STATUS_LIST_SUCCESS =
  "FETCH_DRIVE_STATUS_LIST_SUCCESS";
export const FETCH_DRIVE_STATUS_LIST_FAILED = "FETCH_DRIVE_STATUS_LIST_FAILED";

export const fetchDriveStatusListProgress = () =>
  action(FETCH_DRIVE_STATUS_LIST_PROGRESS);
export const fetchDriveStatusListSuccess = (data: any[]) =>
  action(FETCH_DRIVE_STATUS_LIST_SUCCESS, { data });
export const fetchDriveStatusListFailed = (errorMessage: string) =>
  action(FETCH_DRIVE_STATUS_LIST_FAILED, { errorMessage });

export const fetchDriveStatusListAsync =
  (companyOutletUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchDriveStatusListProgress());

      const res = await api.get(
        `/dataManagement/get-drive-status?company_outlet_uuid=${companyOutletUuid}`
      );

      const data = res.data.data;
      dispatch(fetchDriveStatusListSuccess(data));
    } catch (err: any) {
      dispatch(fetchDriveStatusListFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong!",
          displayAs: "snackbar",
        })
      );
    }
  };

// **************************************************Drive Status Category List**************************************************

export const FETCH_DRIVE_STATUS_CATEGORY_LIST_PROGRESS =
  "FETCH_DRIVE_STATUS_CATEGORY_LIST_PROGRESS";
export const FETCH_DRIVE_STATUS_CATEGORY_LIST_SUCCESS =
  "FETCH_DRIVE_STATUS_CATEGORY_LIST_SUCCESS";
export const FETCH_DRIVE_STATUS_CATEGORY_LIST_FAILED =
  "FETCH_DRIVE_STATUS_CATEGORY_LIST_FAILED";

export const fetchDriveStatusCategoryListProgress = () =>
  action(FETCH_DRIVE_STATUS_CATEGORY_LIST_PROGRESS);
export const fetchDriveStatusCategoryListSuccess = (data: any[]) =>
  action(FETCH_DRIVE_STATUS_CATEGORY_LIST_SUCCESS, { data });
export const fetchDriveStatusCategoryListFailed = (errorMessage: string) =>
  action(FETCH_DRIVE_STATUS_CATEGORY_LIST_FAILED, { errorMessage });

export const fetchDriveStatusCategoryListAsync =
  (companyOutletUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchDriveStatusCategoryListProgress());

      const res = await api.get(
        `/dataManagement/get-drive-status-list?company_outlet_uuid=${companyOutletUuid}`
      );

      const data = res.data.data;
      dispatch(fetchDriveStatusCategoryListSuccess(data));
    } catch (err: any) {
      dispatch(fetchDriveStatusCategoryListFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong!",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_LEAD_STATE = "CLEAR_LEAD_STATE";
export const CLEAR_LEAD = "CLEAR_LEAD";

export const clearLeadsState = () => action(CLEAR_LEAD_STATE);
export const clearLead = () => action(CLEAR_LEAD);

export const CLEAR_ROUGH_ESTIMATE = "CLEAR_ROUGH_ESTIMATE";
export const CLEAR_ROUGH_ESTIMATE_STATE = "CLEAR_ROUGH_ESTIMATE_STATE";

export const clearRoughEstimateState = () => action(CLEAR_ROUGH_ESTIMATE_STATE);
export const clearRoughEstimate = () => action(CLEAR_ROUGH_ESTIMATE);
